import type { TFunction } from "i18next";

import type { PermissionCategory, PermissionItem } from "./types";

interface Subject {
    name: string;
    category: string;
    description: string;
    actions: string[];
}

interface Action {
    name: string;
    description: string;
}

/**
 * Generate a unique permission ID from subject and action
 */
export const generatePermissionId = (subject: string, action: string): string => {
    return `${subject.toLowerCase()}_${action.toLowerCase()}`;
};

/**
 * Generate a human-readable label from subject, action, and their descriptions
 */
export const generatePermissionLabel = (
    t: TFunction,
    subject: string,
    action: string,
    subjectDescription: string,
    _actionDescription: string,
): string => {
    // Get action label from translations, fallback to action name if not found
    const actionLabel = t(`Roles.PermissionActions.${action}`, { defaultValue: action });
    return `${actionLabel} ${subjectDescription || subject}`;
};

/**
 * Get category icon based on category name
 */
export const getCategoryIcon = (category: string): string => {
    const iconMap: Record<string, string> = {
        Financial: "💰",
        Content: "📄",
        "Rights Holders": "👥",
        System: "🔧",
        Integration: "🔌",
    };

    return iconMap[category] || "📋";
};

/**
 * Get category title using translations
 */
export const getCategoryTitle = (t: TFunction, category: string): string => {
    // Map backend category names to translation keys
    const categoryKeyMap: Record<string, string> = {
        Financial: "Financial",
        Content: "Content",
        "Rights Holders": "RightsHolders",
        System: "System",
        Integration: "Integration",
    };

    const key = categoryKeyMap[category] || category;
    return t(`Roles.Categories.${key}`, { defaultValue: category });
};

/**
 * Transform a single subject into permission items
 */
export const subjectToPermissionItems = (t: TFunction, subject: Subject, actions: Action[]): PermissionItem[] => {
    return subject.actions.map((actionName) => {
        const action = actions.find((a) => a.name === actionName);
        return {
            id: generatePermissionId(subject.name, actionName),
            label: generatePermissionLabel(t, subject.name, actionName, subject.description, action?.description || ""),
            action: actionName,
            subject: subject.name,
        };
    });
};

/**
 * Group subjects by category
 */
export const groupSubjectsByCategory = (subjects: Subject[]): Record<string, Subject[]> => {
    return subjects.reduce(
        (acc, subject) => {
            if (!acc[subject.category]) {
                acc[subject.category] = [];
            }
            acc[subject.category].push(subject);
            return acc;
        },
        {} as Record<string, Subject[]>,
    );
};

/**
 * Transform backend subjects into UI permission categories
 * Now organized as: Category > Subject > Actions
 */
export const transformSubjectsToCategories = (
    t: TFunction,
    subjects: Subject[],
    actions: Action[],
): PermissionCategory[] => {
    // Guard against invalid inputs
    if (!Array.isArray(subjects) || !Array.isArray(actions)) {
        console.warn("transformSubjectsToCategories: subjects or actions is not an array", { subjects, actions });
        return [];
    }

    if (subjects.length === 0) {
        console.warn("transformSubjectsToCategories: subjects array is empty");
        return [];
    }

    const groupedSubjects = groupSubjectsByCategory(subjects);

    const result = Object.entries(groupedSubjects).map(([category, categorySubjects]) => {
        // Create a section for each subject within the category
        const sections = categorySubjects.map((subject) => ({
            title: subject.description || subject.name,
            permissions: subjectToPermissionItems(t, subject, actions),
        }));

        const categoryTitle = getCategoryTitle(t, category);

        return {
            id: category.toLowerCase().replace(/\s+/g, "_"),
            icon: getCategoryIcon(category),
            title: categoryTitle,
            description: t("Roles.CategoryDescription", { category: categoryTitle.toLowerCase() }),
            sections,
        };
    });

    return result;
};
